import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "../lib/content";

const LOGO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/e5somxk8_61ada848-88a3-4a61-bf38-c4d30a6e58f8.jpg";
const HERO_PHOTO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/6r4mh0hz_Inner%20Pulse%201.jpg";

export default function Home() {
  const logo = useContent("brand.logo", LOGO_DEFAULT);
  const heroPhoto = useContent("home.hero.photo", HERO_PHOTO_DEFAULT);
  const tagsLine = useContent(
    "home.hero.tags",
    "TECHNO / HARDGROOVE / PSYCHEDELIC / DOWN TEMPO / LIVE SET"
  );
  const subtitle = useContent("home.hero.subtitle", "ESSENCE · DEPTH · INNER EXPERIENCE");
  const sceneText = useContent(
    "home.hero.scene",
    "Artist & catalyst connecting the techno community of the South Island — Aotearoa."
  );
  const nowBookingHero = useContent("home.hero.now_booking", "International Clubs & Festivals");
  const manifestBody = useContent(
    "home.manifest.body",
    "A ritual translated into rhythm. Inner Pulse fuses hypnotic techno, tribal experimental textures and hardgroove into a singular performance, and primal, earthy, psychedelic and organic sounds at the pace of down tempo."
  );
  const nbTitle = useContent("home.now_booking.title", "International Clubs & Festivals");
  const nbBody = useContent(
    "home.now_booking.body",
    "Hybrid live performance available. Pioneer CDJs, Ableton Live, wireless microphone for Inner Dance ceremonies. Managed directly by artist management."
  );
  // Button labels — all editable
  const cta1 = useContent("home.hero.cta1", "Know The Artist");
  const cta2 = useContent("home.hero.cta2", "Festival Pack");
  const cta3 = useContent("home.hero.cta3", "View Events");
  const cta4 = useContent("home.hero.cta4", "Book The Artist");
  const manifestCta = useContent("home.manifest.cta", "Read Biography");
  const nbCta1 = useContent("home.now_booking.cta1", "Booking Inquiry");
  const nbCta2 = useContent("home.now_booking.cta2", "Festival Pack");
  const nbCta3 = useContent("home.now_booking.cta3", "Tech Rider");

  const tags = tagsLine.split("/").map((t) => t.trim()).filter(Boolean);
  const subtitleParts = subtitle.split("·").map((t) => t.trim()).filter(Boolean);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative min-h-[calc(100vh-4rem)] flex items-center px-6 md:px-12 overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 opacity-55"
          style={{
            backgroundImage: `url(${heroPhoto})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
            filter: "grayscale(100%) contrast(1.15) brightness(0.55)",
            maskImage:
              "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.6) 45%, transparent 90%)",
            WebkitMaskImage:
              "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.6) 45%, transparent 90%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 25% 55%, rgba(160, 110, 220, 0.07) 0%, transparent 55%)",
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-[1600px] mx-auto w-full grid md:grid-cols-12 gap-8 items-center pt-12 pb-20">
          <div className="md:col-span-9 flex flex-col gap-8">
            <div className="flex items-center gap-3" data-testid="hero-badge">
              <span className="w-8 h-px bg-silver-400" />
              <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
                {tagsLine}
              </span>
            </div>

            <h1
              className="font-title text-silver-glow text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-[0.06em]"
              data-testid="hero-title"
            >
              INNER
              <br />
              <span className="text-silver-200">PULSE</span>
            </h1>

            <div
              className="flex flex-wrap items-center gap-3 text-silver-400 font-title text-[0.7rem] tracking-[0.32em]"
              data-testid="hero-subtitle"
            >
              {subtitleParts.map((p, i) => (
                <React.Fragment key={i}>
                  <span>{p}</span>
                  {i < subtitleParts.length - 1 && <span className="text-silver-700">·</span>}
                </React.Fragment>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 mt-4" data-testid="hero-cta">
              <Link to="/about" className="btn-sharp" data-testid="cta-know">
                {cta1}
              </Link>
              <Link to="/inner-dance" className="btn-ghost" data-testid="cta-festival-pack">
                {cta2} <ArrowUpRight size={14} />
              </Link>
              <Link to="/events" className="btn-ghost" data-testid="cta-events">
                {cta3} <ArrowUpRight size={14} />
              </Link>
              <Link to="/booking" className="btn-ghost" data-testid="cta-booking">
                {cta4} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <div className="md:col-span-3 flex flex-col gap-6 md:pl-6 md:border-l md:border-silver-800">
            <div data-testid="hero-meta-scene">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-2">
                / SCENE
              </div>
              <p className="font-body text-silver-400 text-xs leading-relaxed">
                {sceneText}
              </p>
            </div>
            <div data-testid="hero-meta-nowbooking">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-2">
                / NOW BOOKING
              </div>
              <p className="font-body text-silver-200 text-xs">
                {nowBookingHero}
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-drift">
          <span className="font-title text-[0.58rem] tracking-[0.4em] text-silver-500">
            SCROLL
          </span>
          <div className="w-px h-8 bg-gradient-to-b from-silver-400 to-transparent" />
        </div>
      </section>

      {/* TICKER */}
      <section
        className="hairline-t hairline-b py-4 overflow-hidden"
        data-testid="ticker-section"
      >
        <div className="marquee gap-16 text-silver-500 font-title text-xs tracking-[0.3em]">
          {[...tags, ...tags, ...tags, ...tags, ...tags].map((t, i) => (
            <span key={i} className="flex items-center gap-16">
              {t}
              <span className="w-1 h-1 rounded-full bg-silver-600" />
            </span>
          ))}
        </div>
      </section>

      {/* MANIFEST */}
      <section className="px-6 md:px-12 py-24" data-testid="manifest-section">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-silver-400" />
              <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
                / MANIFEST
              </span>
            </div>
          </div>
          <div className="md:col-span-9 flex flex-col gap-6">
            <p className="font-body text-silver-200 text-lg md:text-2xl leading-relaxed tracking-wide">
              {manifestBody}
            </p>
            <Link to="/about" className="btn-ghost self-start" data-testid="manifest-cta-bio">
              {manifestCta} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* NOW BOOKING */}
      <section
        className="hairline-t px-6 md:px-12 py-24"
        data-testid="now-booking-section"
      >
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="font-title text-[0.62rem] tracking-[0.32em] text-silver-500 uppercase">
              / Now Booking
            </div>
          </div>
          <div className="md:col-span-9 flex flex-col gap-6">
            <h3 className="font-title text-2xl md:text-4xl tracking-[0.08em] text-silver-glow leading-tight">
              {nbTitle}
            </h3>
            <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed max-w-2xl">
              {nbBody}
            </p>
            <div className="flex gap-4 flex-wrap mt-2">
              <Link to="/booking" className="btn-sharp" data-testid="now-booking-cta">
                {nbCta1}
              </Link>
              <Link to="/inner-dance" className="btn-ghost" data-testid="now-festival-pack-cta">
                {nbCta2} <ArrowUpRight size={14} />
              </Link>
              <Link to="/tech-rider" className="btn-ghost" data-testid="now-tr-cta">
                {nbCta3} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS PREVIEW — now 5 incl. Reviews */}
      <section
        className="hairline-t px-6 md:px-12 py-20"
        data-testid="sections-preview"
      >
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-silver-800">
          {[
            { num: "01", title: "Sets", desc: "SoundCloud recordings.", to: "/sets" },
            { num: "02", title: "Videos", desc: "Live footage & identity.", to: "/videos" },
            { num: "03", title: "Events", desc: "Archive 2021—2026.", to: "/events" },
            { num: "04", title: "Inner Dance", desc: "Festival experience pack.", to: "/inner-dance" },
            { num: "05", title: "Reviews", desc: "What people say.", to: "/reviews" },
          ].map((s) => (
            <Link
              key={s.num}
              to={s.to}
              className="group bg-black p-8 hover:bg-silver-900/40 transition-all duration-700 flex flex-col gap-5"
              data-testid={`preview-${s.title.toLowerCase().replace(" ", "-")}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                  / {s.num}
                </span>
                <ArrowUpRight
                  size={16}
                  className="text-silver-500 group-hover:text-silver-100 group-hover:rotate-45 transition-all"
                />
              </div>
              <h2 className="font-title text-2xl tracking-[0.12em] text-silver-glow">
                {s.title}
              </h2>
              <p className="font-body text-xs text-silver-400 leading-relaxed">
                {s.desc}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* LOGO LOCKUP — logo only, centered, no text */}
      <section
        className="hairline-t px-6 md:px-12 py-24 flex flex-col items-center justify-center"
        data-testid="home-logo-lockup"
      >
        <img
          src={logo}
          alt="Inner Pulse"
          className="w-[260px] h-[260px] md:w-[340px] md:h-[340px] object-cover rounded-full opacity-95 hover:opacity-100 transition-opacity duration-700"
          style={{
            filter: "drop-shadow(0 0 40px rgba(170, 110, 220, 0.18))",
          }}
        />
      </section>
    </div>
  );
}
